import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { logger } from "../lib/logger";
import { CurrencyDollar, Package, Users, Warning, TrendUp, Wallet, Lock, LockOpen, EyeSlash, Sparkle, Clock, ChartBar, Star, TrendDown, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const fmt = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n || 0);
const UNLOCK_TTL_MS = 15 * 60 * 1000; // 15 minutes
const UNLOCK_KEY = "criye_stats_unlock_ts";

const monthName = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("es-AR", { month: "short", year: "2-digit" });
};
const monthNameLong = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("es-AR", { month: "long", year: "numeric" });
};

const Stat = ({ icon: Icon, label, value, color = "turquoise", testid }) => (
  <div className="glass-card rounded-xl p-6 hover:border-white/20 transition" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-${color}/10`} style={{
        background: color === "turquoise" ? "rgba(0,255,212,0.1)" : color === "cyan" ? "rgba(0,176,255,0.1)" : color === "amber" ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.05)"
      }}>
        <Icon size={20} weight="duotone" style={{
          color: color === "turquoise" ? "#00FFD4" : color === "cyan" ? "#00B0FF" : color === "amber" ? "#F59E0B" : "#FFFFFF"
        }} />
      </div>
    </div>
    <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-mono mb-1">{label}</p>
    <p className="text-3xl font-mono font-medium text-white tabular-nums">{value}</p>
  </div>
);

const LockedStat = ({ icon: Icon, label, value, color = "turquoise", testid, locked, onUnlock, onLock }) => (
  <div className="glass-card rounded-xl p-6 relative overflow-hidden group" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{
        background: color === "turquoise" ? "rgba(0,255,212,0.1)" : "rgba(255,255,255,0.05)"
      }}>
        <Icon size={20} weight="duotone" style={{
          color: color === "turquoise" ? "#00FFD4" : "#FFFFFF"
        }} />
      </div>
      {!locked && (
        <button
          onClick={onLock}
          data-testid={`${testid}-lock-btn`}
          className="text-zinc-500 hover:text-turquoise transition opacity-0 group-hover:opacity-100"
          title="Ocultar"
        >
          <EyeSlash size={16} weight="duotone" />
        </button>
      )}
    </div>
    <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-mono mb-1">{label}</p>
    {locked ? (
      <div className="flex items-center justify-between">
        <p className="text-3xl font-mono font-medium text-white/30 tabular-nums select-none" style={{ filter: "blur(8px)" }}>
          $ ●●●●●
        </p>
        <button
          onClick={onUnlock}
          data-testid={`${testid}-unlock-btn`}
          className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-turquoise hover:text-white transition px-3 py-1.5 rounded-md border border-turquoise/30 hover:border-turquoise hover:bg-turquoise/5"
        >
          <Lock size={12} weight="duotone" /> Desbloquear
        </button>
      </div>
    ) : (
      <p className="text-3xl font-mono font-medium text-white tabular-nums">{value}</p>
    )}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [sensitive, setSensitive] = useState(null);
  const [shift, setShift] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [monthDetail, setMonthDetail] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [compareMonth, setCompareMonth] = useState(null);
  const [compareDetail, setCompareDetail] = useState(null);
  const [stars, setStars] = useState([]);
  const [publicTop, setPublicTop] = useState({ month: null, top_products: [] });
  const [publicMonth, setPublicMonth] = useState(null);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const { user } = useAuth();

  const selectMonth = async (month) => {
    setSelectedMonth(month);
    setCompareMonth(null);
    setCompareDetail(null);
    if (!sensitive) {
      setMonthDetail(null);
      await loadPublicTop(month);
      return;
    }
    setLoadingMonth(true);
    try {
      const password = sessionStorage.getItem("criye_stats_pwd");
      const { data } = await api.post("/dashboard/stats/monthly", { password, month });
      setMonthDetail(data.month_detail);
    } catch (e) {
      logger.error("Month detail failed:", e);
    } finally {
      setLoadingMonth(false);
    }
  };

  const loadPublicTop = async (month) => {
    setLoadingMonth(true);
    try {
      const { data } = await api.get("/dashboard/top-products", { params: month ? { month } : {} });
      setPublicTop(data);
      setPublicMonth(data.month);
    } catch (e) {
      logger.error("Public top products failed:", e);
    } finally {
      setLoadingMonth(false);
    }
  };

  const selectCompare = async (month) => {
    setCompareMonth(month);
    if (!month) { setCompareDetail(null); return; }
    try {
      const password = sessionStorage.getItem("criye_stats_pwd");
      const { data } = await api.post("/dashboard/stats/monthly", { password, month });
      setCompareDetail(data.month_detail);
    } catch (e) {
      logger.error("Compare month failed:", e);
    }
  };

  const fetchSensitive = async (password) => {
    const { data } = await api.post("/dashboard/stats/sensitive", { password });
    setSensitive(data);
    sessionStorage.setItem(UNLOCK_KEY, String(Date.now()));
    sessionStorage.setItem("criye_stats_pwd", password);
    try {
      const m = await api.post("/dashboard/stats/monthly", { password });
      setMonthly(m.data.monthly);
    } catch (e) {
      logger.error("Monthly stats failed:", e);
    }
  };

  // Cuando se desbloquea y ya hay monthly, seleccionar el último mes automáticamente
  useEffect(() => {
    if (sensitive && monthly && monthly.length > 0 && !selectedMonth) {
      const lastMonth = monthly[monthly.length - 1].month;
      selectMonth(lastMonth);
    }
  }, [sensitive, monthly]);

  useEffect(() => {
    (async () => {
      try {
        const [s, ls, rs] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/products/low-stock"),
          api.get("/sales"),
        ]);
        setStats(s.data);
        setLowStock(ls.data);
        setRecentSales(rs.data.slice(0, 6));
      } catch (e) {
        logger.error("Dashboard load failed:", e);
      }
      try {
        const { data } = await api.get("/shift-handovers/current");
        setShift(data);
      } catch (e) {
        logger.error("Shift load failed:", e);
      }
      // Ranking + estrellas públicos (sin contraseña)
      try {
        const { data } = await api.get("/dashboard/star-products");
        setStars(data.stars || []);
      } catch (e) {
        logger.error("Star products failed:", e);
      }
      await loadPublicTop();
    })();
    // Auto-unlock if within TTL
    const ts = parseInt(sessionStorage.getItem(UNLOCK_KEY) || "0", 10);
    const savedPwd = sessionStorage.getItem("criye_stats_pwd");
    if (ts && savedPwd && Date.now() - ts < UNLOCK_TTL_MS) {
      fetchSensitive(savedPwd).catch(() => {
        sessionStorage.removeItem(UNLOCK_KEY);
        sessionStorage.removeItem("criye_stats_pwd");
      });
    }
    // Auto-relock after TTL
    const interval = setInterval(() => {
      const t = parseInt(sessionStorage.getItem(UNLOCK_KEY) || "0", 10);
      if (t && Date.now() - t >= UNLOCK_TTL_MS) {
        setSensitive(null);
        setMonthly(null);
        setMonthDetail(null);
        setSelectedMonth(null);
        setCompareMonth(null);
        setCompareDetail(null);
        // stars y publicTop quedan visibles (no requieren contraseña)
        sessionStorage.removeItem(UNLOCK_KEY);
        sessionStorage.removeItem("criye_stats_pwd");
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const submitPwd = async (e) => {
    e.preventDefault();
    setPwdError("");
    setUnlocking(true);
    try {
      await fetchSensitive(pwd);
      setUnlockOpen(false);
      setPwd("");
    } catch (err) {
      setPwdError(err?.response?.data?.detail || "Error al desbloquear");
    } finally {
      setUnlocking(false);
    }
  };

  const lockNow = () => {
    setSensitive(null);
    setMonthly(null);
    setMonthDetail(null);
    setSelectedMonth(null);
    setCompareMonth(null);
    setCompareDetail(null);
    // stars y publicTop quedan visibles (no requieren contraseña)
    sessionStorage.removeItem(UNLOCK_KEY);
    sessionStorage.removeItem("criye_stats_pwd");
  };

  const monthLabel = `Ventas de ${new Date().toLocaleDateString("es-AR", { month: "long" })}`;

  const AR_TZ = "America/Argentina/Buenos_Aires";
  const hourAR = parseInt(new Date().toLocaleString("en-US", { timeZone: AR_TZ, hour: "numeric", hour12: false }), 10) % 24;
  const timeGreeting = hourAR >= 6 && hourAR < 12 ? "Buen día" : hourAR >= 12 && hourAR < 20 ? "Buenas tardes" : "Buenas noches";
  const displayName = shift?.on_shift ? shift.employee : user?.name?.split(' ')[0];
  const sinceDate = shift?.since ? new Date(shift.since) : null;
  const isTodayAR = sinceDate
    && sinceDate.toLocaleDateString("es-AR", { timeZone: AR_TZ }) === new Date().toLocaleDateString("es-AR", { timeZone: AR_TZ });
  const sinceLabel = sinceDate
    ? (isTodayAR
        ? `desde las ${sinceDate.toLocaleTimeString("es-AR", { timeZone: AR_TZ, hour: "2-digit", minute: "2-digit" })}`
        : `desde el ${sinceDate.toLocaleDateString("es-AR", { timeZone: AR_TZ, day: "numeric", month: "short" })}`)
    : "";

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="dashboard-page">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-2">Vista general</p>
        <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight">
          {timeGreeting}, <span className="text-brand-gradient" data-testid="dashboard-greeting-name">{displayName}</span>
        </h1>
        <p className="text-zinc-400 mt-2">Control en tiempo real de tu negocio.</p>
        {shift?.on_shift && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono text-cyan-400 border border-cyan-500/20 bg-cyan-500/5 rounded-full px-3 py-1" data-testid="shift-active-badge">
            <Clock size={13} weight="duotone" /> Turno de {shift.employee} {sinceLabel}
          </p>
        )}
        {shift?.message && (
          <div className="mt-4 flex items-start gap-3 max-w-2xl border border-turquoise/15 bg-turquoise/[0.04] rounded-lg px-4 py-3" data-testid="motivational-banner">
            <Sparkle size={18} weight="fill" className="text-turquoise flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300 italic">{shift.message}</p>
          </div>
        )}
        {stars.length > 0 && (
          <div className="mt-4 border border-amber-400/30 bg-gradient-to-r from-amber-400/10 via-amber-400/5 to-transparent rounded-lg px-4 py-3 max-w-2xl" data-testid="star-products-banner">
            <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-1.5">
              <Star size={16} weight="fill" /> ¡Producto Estrella del mes!
            </div>
            <ul className="space-y-1" data-testid="star-products-list">
              {stars.map((s, i) => (
                <li key={s.name} className="text-sm text-zinc-200 flex items-center gap-2" data-testid={`star-product-${i}`}>
                  <span className="text-amber-400 font-mono text-xs">#{s.rank}</span>
                  <span className="font-medium truncate">{s.name}</span>
                  <span className="text-xs text-zinc-500 font-mono flex-shrink-0">· {s.qty} u. · {fmt(s.revenue)}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-amber-200/70 italic mt-1.5">Entra al top 3 por primera vez.</p>
          </div>
        )}
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={CurrencyDollar} label="Ventas de hoy" value={fmt(stats?.today_total)} color="turquoise" testid="stat-today" />
        <Stat icon={TrendUp} label="Operaciones hoy" value={stats?.today_count ?? 0} color="cyan" testid="stat-today-count" />
        <Stat icon={Package} label="Productos en stock" value={stats?.total_products ?? 0} color="white" testid="stat-products" />
        <Stat icon={Warning} label="Stock bajo" value={stats?.low_stock_count ?? 0} color="amber" testid="stat-low-stock" />
        <Stat icon={Users} label="Clientes" value={stats?.total_customers ?? 0} color="cyan" testid="stat-customers" />
        <Stat icon={Wallet} label="Saldo en billeteras" value={fmt(stats?.total_wallet_balance)} color="turquoise" testid="stat-wallets" />
        <LockedStat
          icon={TrendUp}
          label="Ventas totales"
          value={fmt(sensitive?.total_sales)}
          color="white"
          testid="stat-total-sales"
          locked={!sensitive}
          onUnlock={() => { setUnlockOpen(true); setPwdError(""); }}
          onLock={lockNow}
        />
        <LockedStat
          icon={CurrencyDollar}
          label={monthLabel}
          value={fmt(sensitive?.month_total)}
          color="turquoise"
          testid="stat-month-sales"
          locked={!sensitive}
          onUnlock={() => { setUnlockOpen(true); setPwdError(""); }}
          onLock={lockNow}
        />
      </section>

      <section className="glass-card rounded-xl p-6" data-testid="monthly-stats-section">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <ChartBar size={20} weight="duotone" style={{ color: "#00FFD4" }} /> Ventas por mes
          </h2>
          {((sensitive && monthly && monthly.length > 0) || (!sensitive && publicTop.top_products.length > 0)) && (
            <select
              value={selectedMonth || publicTop.month || ""}
              onChange={(e) => e.target.value && selectMonth(e.target.value)}
              data-testid="month-select"
              className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm outline-none focus:border-turquoise/50 transition"
            >
              <option value="" disabled>Elegir mes...</option>
              {(() => {
                const months = sensitive && monthly ? [...monthly].map((m) => m.month) : [publicTop.month];
                const allMonths = [...new Set(months)].filter(Boolean).sort().reverse();
                return allMonths.map((m) => (
                  <option key={m} value={m}>{monthNameLong(m)}</option>
                ));
              })()}
            </select>
          )}
        </div>

        {sensitive && monthly && monthly.length > 0 && (
          <div className="h-56" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthName} tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis
                  tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)}
                  tick={{ fill: "#71717a", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,255,212,0.05)" }}
                  contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(0,255,212,0.2)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#00FFD4" }}
                  formatter={(value) => [fmt(value), "Ventas"]}
                  labelFormatter={(l) => monthNameLong(l)}
                />
                <Bar dataKey="total" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(d) => d?.month && selectMonth(d.month)}>
                  {monthly.map((m) => (
                    <Cell key={m.month} fill={m.month === selectedMonth ? "#00FFD4" : "rgba(0,176,255,0.55)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {sensitive && monthly && monthly.length > 0 && (
          <p className="text-[11px] text-zinc-600 font-mono mt-2">Tocá una barra o elegí un mes para ver el detalle.</p>
        )}

        {loadingMonth && <p className="text-sm text-zinc-500 mt-4" data-testid="month-loading">Cargando detalle...</p>}

        {/* Ranking de productos (público, sin contraseña) */}
        {((!sensitive && publicTop.top_products.length > 0) || (sensitive && monthDetail && !loadingMonth)) && (
          <div className="mt-5" data-testid="top-products-section">
            {sensitive && monthDetail && !loadingMonth ? (
              <div className="grid md:grid-cols-[220px_1fr] gap-5 items-start" data-testid="month-detail">
                <div className="border border-turquoise/20 bg-turquoise/5 rounded-lg p-4">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono capitalize">{monthNameLong(monthDetail.month)}</p>
                  <p className="text-2xl font-mono font-bold text-turquoise mt-1" data-testid="month-detail-total">{fmt(monthDetail.total)}</p>
                  <p className="text-xs text-zinc-400 mt-1">{monthDetail.count} operaciones</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Productos más vendidos</p>
                  {monthDetail.top_products.length === 0 ? (
                    <p className="text-sm text-zinc-500">Sin ventas ese mes.</p>
                  ) : (
                    <ul className="space-y-1.5" data-testid="top-products-list">
                      {monthDetail.top_products.map((p, i) => (
                        <li key={p.name} className="relative flex items-center justify-between px-3 py-2 rounded-md bg-white/[0.02] border border-white/5 overflow-hidden" data-testid={`top-product-${i}`}>
                          <div
                            className="absolute inset-y-0 left-0 bg-cyan-500/10"
                            style={{ width: `${Math.max(4, (p.qty / monthDetail.top_products[0].qty) * 100)}%` }}
                          />
                          <div className="relative flex items-center gap-2 min-w-0">
                            <span className={`text-xs font-mono w-6 flex-shrink-0 ${i < 3 ? "text-turquoise" : "text-zinc-600"}`}>#{i + 1}</span>
                            <span className="text-sm truncate">{p.name}</span>
                          </div>
                          <div className="relative text-right flex-shrink-0 ml-3">
                            <span className="text-sm font-mono text-white">{p.qty} u.</span>
                            <span className="text-xs font-mono text-zinc-500 ml-2">{fmt(p.revenue)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono capitalize">
                    Productos más vendidos — {monthNameLong(publicTop.month || publicMonth)}
                  </p>
                  {!sensitive && (
                    <button
                      onClick={() => { setUnlockOpen(true); setPwdError(""); }}
                      data-testid="monthly-unlock-btn"
                      className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-turquoise hover:text-white transition px-3 py-1 rounded-md border border-turquoise/30 hover:border-turquoise hover:bg-turquoise/5"
                    >
                      <Lock size={11} weight="duotone" /> Ver montos
                    </button>
                  )}
                </div>
                <ul className="space-y-1.5" data-testid="top-products-list-public">
                  {publicTop.top_products.map((p, i) => (
                    <li key={p.name} className="relative flex items-center justify-between px-3 py-2 rounded-md bg-white/[0.02] border border-white/5 overflow-hidden" data-testid={`top-product-${i}`}>
                      <div
                        className="absolute inset-y-0 left-0 bg-cyan-500/10"
                        style={{ width: `${Math.max(4, (p.qty / publicTop.top_products[0].qty) * 100)}%` }}
                      />
                      <div className="relative flex items-center gap-2 min-w-0">
                        <span className={`text-xs font-mono w-6 flex-shrink-0 ${i < 3 ? "text-turquoise" : "text-zinc-600"}`}>#{i + 1}</span>
                        <span className="text-sm truncate">{p.name}</span>
                      </div>
                      <span className="relative text-sm font-mono text-white flex-shrink-0 ml-3">{p.qty} u.</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

            {monthDetail && !loadingMonth && (
              <div className="mt-5 pt-5 border-t border-white/5" data-testid="compare-section">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Comparar con otro mes</p>
                  <select
                    value={compareMonth || ""}
                    onChange={(e) => selectCompare(e.target.value || null)}
                    data-testid="compare-select"
                    className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm outline-none focus:border-cyan-500/50 transition"
                  >
                    <option value="">Sin comparar</option>
                    {[...monthly].reverse().filter((m) => m.month !== monthDetail.month).map((m) => (
                      <option key={m.month} value={m.month}>{monthNameLong(m.month)}</option>
                    ))}
                  </select>
                </div>
                {compareDetail && (() => {
                  const base = compareDetail.total;
                  const diff = monthDetail.total - base;
                  const pct = base > 0 ? (diff / base) * 100 : (monthDetail.total > 0 ? 100 : 0);
                  const up = diff >= 0;
                  const opsDiff = monthDetail.count - compareDetail.count;
                  const opsPct = compareDetail.count > 0 ? (opsDiff / compareDetail.count) * 100 : (monthDetail.count > 0 ? 100 : 0);
                  const opsUp = opsDiff >= 0;
                  return (
                    <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch" data-testid="compare-detail">
                      {/* Mes base (comparación) */}
                      <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono capitalize">{monthNameLong(compareDetail.month)}</p>
                        <p className="text-xl font-mono font-bold text-zinc-200 mt-1" data-testid="compare-base-total">{fmt(compareDetail.total)}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{compareDetail.count} operaciones</p>
                      </div>
                      {/* Diferencia % */}
                      <div className="flex md:flex-col items-center justify-center gap-3 md:gap-1 px-4">
                        <ArrowRight size={18} className="text-zinc-600 md:hidden" />
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-mono font-semibold ${up ? "border-turquoise/40 bg-turquoise/10 text-turquoise" : "border-red-500/40 bg-red-500/10 text-red-400"}`} data-testid="compare-diff-pct">
                          {up ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                          {up ? "+" : ""}{pct.toFixed(1)}%
                        </div>
                        <p className={`text-[11px] font-mono ${up ? "text-turquoise/70" : "text-red-400/70"}`}>
                          {up ? "+" : ""}{fmt(diff)}
                        </p>
                        <p className={`text-[10px] font-mono ${opsUp ? "text-cyan-400/80" : "text-red-400/70"}`}>
                          {opsUp ? "+" : ""}{opsDiff} ops ({opsUp ? "+" : ""}{opsPct.toFixed(0)}%)
                        </p>
                      </div>
                      {/* Mes seleccionado */}
                      <div className="border border-turquoise/30 bg-turquoise/5 rounded-lg p-4">
                        <p className="text-[10px] uppercase tracking-widest text-turquoise font-mono capitalize">{monthNameLong(monthDetail.month)}</p>
                        <p className="text-xl font-mono font-bold text-turquoise mt-1">{fmt(monthDetail.total)}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">{monthDetail.count} operaciones</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-semibold">Stock bajo</h2>
            <Link to="/inventario" className="text-xs text-turquoise hover:underline font-mono">VER TODO →</Link>
          </div>
          {lowStock.length === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center">Todo el stock está en niveles óptimos.</p>
          ) : (
            <ul className="space-y-2">
              {lowStock.slice(0, 6).map((p) => (
                <li key={p.product_id} className="flex items-center justify-between p-3 rounded-md bg-white/[0.02] border border-white/5">
                  <div>
                    <p className="text-sm font-medium text-white">{p.name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{p.category} · {p.supplier_name || "Sin proveedor"}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-lg text-amber-400">{p.stock}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600">de {p.stock_min} min</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-semibold">Últimas ventas</h2>
            <Link to="/ventas" className="text-xs text-turquoise hover:underline font-mono">VER TODO →</Link>
          </div>
          {recentSales.length === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center">Aún no hay ventas registradas.</p>
          ) : (
            <ul className="space-y-2">
              {recentSales.map((s) => (
                <li key={s.sale_id} className="flex items-center justify-between p-3 rounded-md bg-white/[0.02] border border-white/5">
                  <div>
                    <p className="text-sm font-medium text-white">{s.customer_name}</p>
                    <p className="text-xs text-zinc-500 font-mono">{s.items.length} item(s) · {s.seller_name}</p>
                  </div>
                  <p className="font-mono text-lg text-turquoise">{fmt(s.total)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {unlockOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="stats-unlock-modal">
          <form onSubmit={submitPwd} className="glass-card rounded-xl max-w-sm w-full p-6 border border-white/10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-turquoise/10">
                <LockOpen size={20} weight="duotone" style={{ color: "#00FFD4" }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono">Acceso restringido</p>
                <h3 className="text-lg font-heading font-bold">Desbloquear estadísticas</h3>
              </div>
            </div>
            <p className="text-sm text-zinc-400 mb-4">Ingresá la contraseña para ver Ventas Totales y del Mes. El acceso permanece por 15 minutos.</p>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Contraseña"
              autoFocus
              data-testid="stats-pwd-input"
              className="w-full px-4 py-3 rounded-md bg-white/[0.03] border border-white/10 focus:border-turquoise outline-none text-white font-mono tracking-wider"
            />
            {pwdError && (
              <p className="text-red-400 text-xs mt-3 font-mono" data-testid="stats-pwd-error">{pwdError}</p>
            )}
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => { setUnlockOpen(false); setPwd(""); setPwdError(""); }}
                data-testid="stats-pwd-cancel"
                className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5 transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={unlocking || !pwd}
                data-testid="stats-pwd-submit"
                className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {unlocking ? "..." : "Desbloquear"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
